import { Image } from '@chakra-ui/react';
import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import {
  Box,
  Text,
  Heading,
  theme,
  Button,
  ButtonProps,
  Flex,
} from '@everlywell/ui-kit';
import DynamicIcon, { IconKey } from 'components/DynamicIcon';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import SectionHeader from 'components/PageSections/components/SectionHeader';
import ToolCardV2 from 'components/ToolCardV2';
import { Link } from 'gatsby';
import React, { MouseEvent } from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';
import { isFullUrl } from 'utils/helpers';
import { replaceRedirectionLinkRoot } from 'utils/helpers/replaceRedirectionLinkRoot';
import useMdpModals from 'utils/hooks/useMdpModals';
import { ImageType } from 'utils/types';
import { CallToAction, ContentfulFlag } from 'utils/types/contentfulTypes';

export interface BannerAndToolCardsProps {
  eyebrow: string;
  title: string;
  bgColor?: string;
  cardBgColor?: string;
  textColor?: string;
  headline?: ContentfulRichText;
  flags?: ContentfulFlag[];
  desktopBackgroundImage?: ImageType;
  desktopBackgroundImageAltText?: string;
  centerContent?: boolean;
  bullets:
    | {
        iconName: IconKey;
        title: string;
      }[]
    | null;
  toolCards: {
    title?: string;
    bgColor?: string;
    textColor?: string;
    body?: ContentfulRichText;
    desktopBackgroundImage?: ImageType;
  }[];
  cta?: CallToAction | null;
}

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => (
      <p
        style={{
          fontSize: theme.fontSizes['lg'],
          fontWeight: theme.fontWeights.normal,
          marginBottom: theme.space[3],
        }}
      >
        {children}
      </p>
    ),
    [BLOCKS.HEADING_2]: (_, children) => (
      <Heading
        as="h2"
        fontSize={['xl', '5xl']}
        fontWeight="normal"
        lineHeight={['27px', '41.60px']}
        marginBottom="4"
        color="moonlight.base"
      >
        {children}
      </Heading>
    ),
    [BLOCKS.HEADING_3]: (_, children) => (
      <Heading
        as="h3"
        fontSize={['xl', '2xl']}
        fontWeight="medium"
        lineHeight={['27px', '28px']}
        marginBottom="8"
      >
        {children}
      </Heading>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul
        style={{
          fontSize: theme.fontSizes['lg'],
          listStyleType: 'initial',
          marginInlineStart: '1em',
        }}
      >
        <li style={{ listStyle: 'disc' }}>{children}</li>
      </ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol
        style={{
          fontSize: theme.fontSizes['lg'],
          listStyleType: 'initial',
          marginInlineStart: '1em',
        }}
      >
        <li style={{ listStyle: 'numeric' }}>{children}</li>
      </ol>
    ),
  },
};

/**
 * BannerAndToolCards is a component that displays a banner section with a background image
 * and different options for content: content on the left side with text and HorizontalProductCard,
 * or text on the left side with colorful cards on the right.
 * The different options are defined by a flag in Contentful subsection.
 */
const BannerAndToolCards: React.FC<BannerAndToolCardsProps | null> = (
  props,
) => {
  const { handleMdpCtaClick } = useMdpModals();

  if (!props) {
    return null;
  }

  const {
    eyebrow,
    title,
    bgColor,
    headline,
    desktopBackgroundImage,
    desktopBackgroundImageAltText,
    bullets,
    toolCards,
    cta,
  } = props;

  const onButtonClick = (event: MouseEvent<HTMLButtonElement>) => {
    handleMdpCtaClick({
      event,
      ctaUrl: cta?.url || '',
    });
    trackButtonClick();
  };

  const trackButtonClick = () => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_LINK,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'BannerAndToolCards',
        ctaText: cta?.label || 'No CTA label',
      },
    });
  };

  const hasFullUrl = isFullUrl(cta?.url);

  const linkTo = hasFullUrl
    ? replaceRedirectionLinkRoot(cta?.url || '')
    : cta?.url;

  const buttonProps: ButtonProps = {
    ...(hasFullUrl ? { href: linkTo, as: 'a' } : { to: linkTo, as: Link }),
  };

  return (
    <SectionContainer size={['sm', null, null, 'xl']} centerContent>
      <SectionHeader title={title} eyebrow={eyebrow}></SectionHeader>

      <Box
        background={bgColor}
        display={['flex']}
        flexFlow={['column', 'row']}
        height={['auto', 'auto', '622px']}
        overflow="hidden"
        position={['unset', 'relative']}
        borderRadius={12}
      >
        <Box
          background={bgColor}
          position={['unset', 'absolute']}
          top="0"
          left="225px"
          clipPath="ellipse(20% 60% at 55% 50%)"
          zIndex={['0', '1']}
          height={['auto', '100%']}
          width={['auto', '56%']}
          display={['none', 'none', 'none', 'initial']}
        />

        <Flex
          flex={['unset', 1]}
          padding={['6', '12']}
          order={['2', '1']}
          flexFlow="column"
          alignItems="stretch"
          zIndex={['initial', '1']}
          justifyContent={cta?.label ? 'space-between' : 'center'}
        >
          <Box>
            {headline ? (
              <Box width={['100%', '80%']} marginTop={['6', 'unset']}>
                {renderRichText(headline, options)}
              </Box>
            ) : null}
            {bullets
              ? bullets.map((bullet) => (
                  <Box
                    width="100%"
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    maxWidth="380px"
                    marginBottom={5}
                    key={bullet.title}
                    _last={{ marginBottom: 0 }}
                  >
                    <Box
                      width={['32px']}
                      height={['32px']}
                      marginRight={3}
                      backgroundColor="tints.white"
                      borderRadius="50%"
                      padding={1}
                    >
                      <DynamicIcon
                        icon={bullet.iconName}
                        color="moonlight.base"
                      />
                    </Box>

                    <Text size="lg">{bullet.title}</Text>
                  </Box>
                ))
              : null}
          </Box>
          {cta && cta?.label ? (
            <Button
              variant="primary"
              id={`${title}-${cta?.label}`}
              onClick={onButtonClick}
              marginTop="6"
              width={['100%', null, 'fit-content']}
              {...buttonProps}
            >
              {cta?.label}
            </Button>
          ) : null}
        </Flex>

        <Box
          flex={['unset', 1]}
          display="flex"
          alignItems={['center', 'flex-start', 'center']}
          justifyContent="center"
          order={['1', '2']}
          height={['300px', 'auto']}
          overflow={['unset', 'hidden']}
          position="relative"
        >
          {desktopBackgroundImage ? (
            <Image
              src={desktopBackgroundImage.gatsbyImageData.images.fallback?.src}
              alt={desktopBackgroundImageAltText || ''}
              boxSize="100%"
              objectFit="cover"
              clipPath={['ellipse(63% 71% at 50% 45%)', 'unset']}
            />
          ) : null}
          {toolCards ? <ToolCardV2 toolCards={toolCards} /> : null}
        </Box>
      </Box>
    </SectionContainer>
  );
};

export default BannerAndToolCards;
